import React, {useContext, useRef, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import {withStyles} from "@material-ui/core/styles";
import DataGrid from "../../../controls/DataGrid";

import { useAircraftMovementsList } from "../../../services/aircraft";
import { IconButton } from '@material-ui/core';
import {DeleteOutline, LocalGasStation} from '@material-ui/icons';
import SystemContext from "../../../SystemContext";
import useDeleteAircraftMovementsForLeg from '../../../services/aircraft/useDeleteAircraftMovementsForLeg';
import FuelTooltip from "./FuelTooltip";

const styles = {
  header: {
    "&.ag-header-cell": {
      paddingLeft: 4, paddingRight: 4, fontSize: 12
    }
  },
  column: {
    "&.ag-cell-not-inline-editing": {
      paddingLeft:4, paddingRight: 4
    }
  },
};


const AircraftMovementsList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);

  const props = {
    ..._props
  }
  const { classes } = props;

  const [state, setState] = useState({
    canEdit: props.canEdit||false,
  });

  const dataGridRef = useRef();

  const queryClient = useQueryClient();
  const extraKeys = [props.aircraftTailNumber, 'true'];

  const aircraftMovements = useAircraftMovementsList({queryClient, extraKeys});

  const handleDeleteForLeg = useDeleteAircraftMovementsForLeg();

  const handleRemoveItem = async (data, api, node) => {
    const {tripLegUuid} = data;
    SystemData.confirm(
      `Delete movement`,
      `Are you sure you wish to delete the movement for this flight'`,
      [
        {icon: "check_circle", value: true, label: "Yes", options: {color:"primary"}},
        {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
      ],
      (response) => {
        if (response) {
          handleDeleteForLeg.mutate({tripLegUuid});
        }
      }
    );
  }

  const renderActionButtons = (params) => {
    return (
      <>
        {
          <IconButton
            color="secondary"
            onClick={() => { handleRemoveItem(params.data, params.api, params.node) }}
            disabled={moment.utc(params?.data?.deletedAt, 'YYYY-MM-DD').isBefore(moment.utc('9999-12-31', 'YYYY-MM-DD'))}
            size={"small"}
          >
            <DeleteOutline/>
          </IconButton>
        }
      </>
    )
  }

  const renderFuelButton = (params) => {
    return (
      <>
        {
          <FuelTooltip movement={params.data}>
            <IconButton
              color="primary"
              // onClick={() => { handleRemoveItem(params.data, params.api, params.node) }}
              onClick={() => {}}
              // disabled={moment.utc(params?.data?.deletedAt, 'YYYY-MM-DD').isBefore(moment.utc('9999-12-31', 'YYYY-MM-DD'))}
              disabled={false}
              size={"small"}
            >
              <LocalGasStation/>
            </IconButton>
          </FuelTooltip>
        }
      </>
    )
  }

  const getColumns = (props) => {
    const baseColumn = {
      options: {
        cellClass: classes.column,
        cellStyle:{textDecoration: "inherit",color: "inherit"},
        headerClass: classes.header,
        menuTabs: [],
      },
      editable: false//props.canEdit,
    };

    return [
      {
        ...baseColumn,
        headerName: "Flight",
        field: "flightNumber",
        width: 100,
      },
      {
        ...baseColumn,
        headerName: "Date",
        field: "flightDate",
        width: 120,
        valueFormatter: (params)=> {
          return moment(params.value).format("DD/MM/YYYY");
        }
      },
      {
        ...baseColumn,
        headerName: "Origin",
        field: "originAirportCodeIata",
        width: 80,
      },
      {
        ...baseColumn,
        headerName: "Destination",
        field: "destinationAirportCodeIata",
        width: 120,
      },
      {
        ...baseColumn,
        headerName: "Push Back",
        field: "offBlocksTimeUTC",
        width: 160,
        valueFormatter: (params)=> {
          if(params.value === null) return "";
          return moment(params.value).format("DD/MM/YYYY HH:mm");
        }
      },
      {
        ...baseColumn,
        headerName: "Take Off",
        field: "takeOffTimeUTC",
        width: 160,
        valueFormatter: (params)=> {
          if(params.value === null) return "";
          return params.value ? moment(params.value).format("DD/MM/YYYY HH:mm") : "Taxiing to take-off";
        }
      },
      {
        ...baseColumn,
        headerName: "Landing",
        field: "landingTimeUTC",
        width: 160,
        valueFormatter: (params)=> {
          if (params.data.takeOffTimeUTC !== null)
            return params.value ? moment(params.value).format("DD/MM/YYYY HH:mm") : "En-route";
          else
            return "";
        }
      },
      {
        ...baseColumn,
        headerName: "Blocks On",
        field: "onBlocksTimeUTC",
        width: 160,
        valueFormatter: (params)=> {
          if (params.data.landingTimeUTC !== null)
            return params.value ? moment(params.value).format("DD/MM/YYYY HH:mm") : "Taxiing to stand";
          else
            return "";
        }
      },
      {
        ...baseColumn,
        headerName: "Fuel",
        field: "movementFuelKg",
        type: "rightAligned",
        options: { cellClass: `ag-right-aligned-cell ${classes.column}`, headerClass: `ag-right-aligned-header ${classes.header}` },
        valueFormatter: (params)=> {
          return params.value ? params.value.toLocaleString() : "";
        },
        flex: 1,
      },
      {
        ...baseColumn,
        field: 'fuelInfo',
        headerName: ' ',
        editable: false,
        cellRenderer: {
          component: "renderFuelButton",
          params: {
            disabled: !props.canEdit,
          }
        },
        maxWidth: 40,
        cellStyle: { border: "none" },
      },
      {
        ...baseColumn,
        field: 'deleteAction',
        headerName: ' ',
        editable: false,
        cellRenderer: {
          component: "renderActionButtons",
          params: {
            disabled: !props.canEdit,
          }
        },
        maxWidth: 40,
        cellStyle: { border: "none" },
      },
    ];
  };

  const dataGridComponents = {renderActionButtons, renderFuelButton};

  return (isAuthenticated &&
    <div style={{width: props.width?props.width:"100%", ...props.style}}>
      <DataGrid
        title="AircraftMovements"
        ref={dataGridRef}
        components={dataGridComponents}
        columns={getColumns({canEdit: state.canEdit})}
        rows={aircraftMovements.data}
        height={props.height||"300px"}
        options={{
          getRowStyle: (rowData) => {console.log(rowData?.data?.deletedAt); return moment.utc(rowData?.data?.deletedAt, 'YYYY-MM-DD').isBefore(moment.utc('9999-12-31', 'YYYY-MM-DD'))?{textDecoration: "line-through", color:"rgba(0,0,0,0.5)"}:null},
          singleClickEdit: false,
          // editType:'fullRow',
          // onCellValueChanged: ()=>{}, //prevent default onCellValueChanged
          defaultColDef: {
            editable: false,
            resizable: false,
          }
        }}
      />
    </div>
  );

}

export default withStyles(styles)(AircraftMovementsList);
